// WHAT: Body composition visualization tab with pie chart and individual metric charts
// WHY:  Displays comprehensive body composition metrics from Withings measurements with norm ranges
// DOMAIN: Health → Withings → Body Composition Analysis
// DEPENDENCIES: Recharts library, WithingsMeasurement types, MetricChart component
// RISKS:
//   - NOTE: Pie chart data calculation assumes latest measurement exists - may crash if empty
//   - NOTE: MetricChart component requires specific data structure - type safety may be insufficient
//   - NOTE: Norm ranges are hardcoded - may not be accurate for all user demographics

import React from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

import { Card } from "@/components/ui/Card";

import { type WithingsMeasurement } from "../../types";
import { MetricChart } from "../molecules/MetricChart";
import { WithingsLoader } from "../ui";

interface BodyCompositionTabProperties {
  isLoading?: boolean;
  measurements: WithingsMeasurement[];
}

/**
 * WHAT:
 *   Body composition visualization tab with pie chart and individual metric charts.
 *
 * WHY:
 *   Displays comprehensive body composition metrics from Withings measurements with norm ranges.
 *
 * PROPS:
 *   - measurements: WithingsMeasurement[] — Array of body composition measurements (sorted by date, latest first)
 *   - isLoading?: boolean — Whether measurements are currently being loaded
 *
 * BEHAVIOR:
 *   - Shows loading spinner if no measurements and still loading
 *   - Displays pie chart with latest measurement composition data
 *   - Renders individual metric charts for weight, body fat, muscle mass, etc.
 *   - Uses MetricChart component for consistent chart styling
 *   - Assumes measurements[0] is the latest measurement
 *
 * RISKS:
 *   - NOTE: Pie chart data calculation assumes latest measurement exists - may crash if empty
 *   - NOTE: MetricChart component requires specific data structure - type safety may be insufficient
 *   - NOTE: Norm ranges are hardcoded - may not be accurate for all user demographics
 *   - WARN: No error handling if measurements array is malformed
 *
 * @param root0
 *
 * @param root0.measurements
 *
 * @param root0.isLoading
 *
 * @returns {JSX.Element} Body composition tab component with pie chart and metric charts
 *
 */
export const BodyCompositionTab: React.FC<BodyCompositionTabProperties> = ({
  isLoading = false,
  measurements,
}) => {
  // NOTE: Assume measurements are sorted by date (latest first) - measurements[0] is most recent
  const latest = measurements[0];

  // NOTE: Show loading state only if no measurements exist and data is still loading
  // This prevents showing empty state during initial load
  if (isLoading && measurements.length === 0) {
    return <WithingsLoader />;
  }

  // NOTE: Build pie chart data array from latest measurement
  // WARN: Assumes latest exists - will create empty array if measurements array is empty
  // NOTE: Mix of absolute values (kg) and percentages - chart handles both
  const compositionData = latest
    ? [
        { color: "#F59E0B", name: "Fat Mass", value: latest.fat_mass_kg }, // NOTE: Orange for fat
        {
          color: "#8B5CF6",
          name: "Lean Mass (calculated)",
          value: latest.fat_free_mass_percent,
        }, // NOTE: Purple for lean mass (percentage)
        { color: "#10B981", name: "Muscle Mass", value: latest.muscle_mass_kg }, // NOTE: Green for muscle
        { color: "#6366F1", name: "Bone Mass", value: latest.bone_mass_kg }, // NOTE: Indigo for bone
        {
          color: "#06B6D4",
          name: "Body Water",
          value: latest.body_water_percent,
        }, // NOTE: Cyan for water (percentage)
      ]
    : [];

  return (
    <div className="space-y-6">
      {/* Pie Chart - Overview */}
      <Card className="bg-slate-800/50 border-slate-700 text-white">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-6">Body Composition - Overview</h3>
          <ResponsiveContainer height={300} width="100%">
            <PieChart>
              <Pie
                cx="50%"
                cy="50%"
                data={compositionData}
                dataKey="value"
                label={({ name, value }) => {
                  if (typeof value !== "number") {
                    return `${name}: N/A`;
                  }
                  if (name === "Lean Mass (calculated)" || name === "Body Water") {
                    return `${name}: ${value.toFixed(1)}%`;
                  }
                  return `${name}: ${value.toFixed(1)}kg`;
                }}
                outerRadius={100}
              >
                {compositionData.map((entry) => (
                  <Cell fill={entry.color} key={`cell-${entry.name}`} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Individual Metric Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricChart
          color="#3B82F6"
          data={measurements}
          dataKey="weight_kg"
          normRangeKey="weight"
          title="Weight"
          unit="kg"
        />

        <MetricChart
          color="#EF4444"
          data={measurements}
          dataKey="body_fat_percent"
          normRangeKey="bodyFat"
          title="Body Fat"
          unit="%"
        />

        <MetricChart
          color="#10B981"
          data={measurements}
          dataKey="muscle_mass_kg"
          normRangeKey="muscleMass"
          title="Muscle Mass"
          unit="kg"
        />

        <MetricChart
          color="#F59E0B"
          data={measurements}
          dataKey="fat_mass_kg"
          normRangeKey="fatMass"
          title="Fat Mass"
          unit="kg"
        />

        <MetricChart
          color="#06B6D4"
          data={measurements}
          dataKey="body_water_percent"
          normRangeKey="bodyWater"
          title="Body Water"
          unit="%"
        />

        <MetricChart
          color="#8B5CF6"
          data={measurements}
          dataKey="bone_mass_kg"
          normRangeKey="boneMass"
          title="Bone Mass"
          unit="kg"
        />

        <MetricChart
          color="#EC4899"
          data={measurements}
          dataKey="visceral_fat"
          normRangeKey="visceralFat"
          title="Visceral Fat"
          unit="Index"
        />

        <MetricChart
          color="#8B5CF6"
          data={measurements}
          dataKey="fat_free_mass_percent"
          normRangeKey="fatFreeMassPercent"
          title="Lean Mass (calculated)"
          unit="%"
        />

        <MetricChart
          color="#06B6D4"
          data={measurements}
          dataKey="bmi"
          normRangeKey="bmi"
          title="BMI"
          unit="BMI"
        />
      </div>
    </div>
  );
};
